import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import {
  Flex,
  Text,
  Box,
  Spinner,
  Button,
  useToastContext,
} from '@resideo/blueprint-react';
import SelectCompanyButton from 'components/SignUp/SelectCompanyButton';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { CompanyInfoValues } from './CompanyInfoForm';
import PageLoading from 'components/common/PageLoading';
import { formatAddressShort } from 'utils/common';
import { useRisClient } from 'hooks/useRisClient';
import {
  PartnerAccountExpertise,
  CountryCode,
} from '@resideo/web-integration-services-api-client';
import * as Sentry from '@sentry/react';

const MAX_COMPANY_MATCHES = 100;

const Container = styled(Flex)`
  justify-content: space-between;
  flex-direction: column;
  align-items: center;
  max-width: 453px;
  margin-left: 100px;
  margin-right: 100px;

  @media (max-width: ${({ theme }) => theme.breakpoints.small}) {
    max-width: 325px;
    margin-left: 8px;
    margin-right: 8px;
  }
`;

const StyledSelectCompanyButton = styled(SelectCompanyButton)`
  margin-top: 16px;
`;

const NextButton = styled(Button)`
  width: 325px;

  @media (max-width: ${({ theme }) => theme.breakpoints.small}) {
    width: 262px;
  }
`;

export interface CompanyInfoJoinExistingProps {
  email: string;
  formValues: CompanyInfoValues;
  partnerAccountMatch: any[];
}

const CompanyInfoJoinExisting = ({
  email,
  formValues,
  partnerAccountMatch,
}: CompanyInfoJoinExistingProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { addToast } = useToastContext();
  const [selectedId, setSelectedId] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const { client } = useRisClient();

  const requestToJoin = useMutation({
    mutationFn: async (partnerAccountId: string) => {
      return client.myProUsers.myProUsersCreateAddRequest({
        partnerAccountId,
        sendToEmail: email,
      });
    },
    onError: () => {
      setSubmitting(false);
      addToast({
        toastType: 'Error',
        message: t('common.toast.error'),
      });
      Sentry.captureMessage('Request to Join Company - Failed', 'error');
    },
    onSuccess: () => {
      Sentry.captureMessage('Request to Join Company - Success', 'info');

      navigate('/sign-up/company-info/landing?requestToJoin=true');
    },
  });

  const createPartnerAccount = useMutation({
    mutationFn: async (vars: { vals: CompanyInfoValues; address }) => {
      return client.myProAccounts.myProAccountsCreatePartnerAccount({
        billToAddress: vars.address,
        contactEmail: email,
        name: vars.vals.name,
        primaryPhoneNumber: vars.vals.primaryPhoneNumber,
        shipToAddress: vars.address,
        partnerTypes: [],
        approvalRequest: null,
        expertise: vars.vals.typeOfBusiness as PartnerAccountExpertise,
      });
    },
    onError: () => {
      setSubmitting(false);
      addToast({
        toastType: 'Error',
        message: t('common.toast.error'),
      });
      Sentry.captureMessage('Create New Company - Failed', 'error');
    },
    onSuccess: () => {
      Sentry.captureMessage(
        noMatchesAvailable
          ? `Created New Company - (Automatic) No Fuzzy Match Results - Success`
          : `Created New Company - (Manual) Selected No Matches - Success`,
        'info'
      );

      navigate('/sign-up/company-info/landing');
    },
  });

  const noMatchesAvailable = partnerAccountMatch.length === 0;

  useEffect(() => {
    // No partner account matches? Call createPartnerAccount right away
    if (noMatchesAvailable) {
      handleSubmit();
    }
  }, []);

  const handleSubmit = async () => {
    setSubmitting(true);

    if (selectedId === '-1' || noMatchesAvailable) {
      const {
        addressLine1,
        addressLine2,
        city,
        countryCode,
        state,
        zipPostalCode,
      } = formValues;

      const address = {
        addressLine1: addressLine1,
        addressLine2: addressLine2,
        city: city,
        countryCode: countryCode as CountryCode,
        stateProvinceRegionCode: state,
        zipPostalCode: zipPostalCode,
      };

      Sentry.addBreadcrumb({
        message: `Creating New Company`,
        level: 'debug',
        data: formValues,
        timestamp: Date.now(),
      });

      createPartnerAccount.mutate({ vals: formValues, address });
    } else {
      Sentry.addBreadcrumb({
        message: `Requesting to Join Company'`,
        level: 'debug',
        data: { partnerAccountId: selectedId, formValues },
        timestamp: Date.now(),
      });

      requestToJoin.mutate(selectedId);
    }
  };

  if (noMatchesAvailable) {
    return <PageLoading />;
  }

  return (
    <Container>
      <Flex flexDirection='column' alignItems='left' paddingTop='medium'>
        <Text lineHeight='1.25' data-test-company-info-join-subheader1>
          {t('signUp.companyInfo.joinExisting.subHeader1')}
        </Text>
        <Text
          textAlign='left'
          lineHeight='1.25'
          paddingTop='medium'
          data-test-company-info-join-subheader2>
          {t('signUp.companyInfo.joinExisting.subHeader2')}
        </Text>
      </Flex>
      {partnerAccountMatch
        .slice(0, MAX_COMPANY_MATCHES - 1)
        .map(({ id, name, shipToAddress }) => (
          <StyledSelectCompanyButton
            key={id}
            id={id}
            name={name}
            address={shipToAddress ? formatAddressShort(shipToAddress) : ''}
            active={id === selectedId}
            onChange={selected => setSelectedId(selected)}
          />
        ))}
      <StyledSelectCompanyButton
        key={-1}
        id={'-1'}
        name={t('signUp.companyInfo.joinExisting.noneMatch')}
        active={'-1' === selectedId}
        onChange={id => setSelectedId(id)}
      />
      <Box>
        <Flex padding='large' justifyContent='center'>
          <NextButton
            className='common-button primary'
            variant='primary'
            type='submit'
            disabled={!selectedId}
            onClick={handleSubmit}
            data-test-submit-button>
            {submitting && (
              <Spinner
                aria-label={t('common.saving')}
                marginX='small'
                color='white'
                verticalAlign='text-top'
              />
            )}
            {!submitting && t('common.next')}
          </NextButton>
        </Flex>
      </Box>
    </Container>
  );
};

export default CompanyInfoJoinExisting;
